import { createSlice, PayloadAction } from '@reduxjs/toolkit'

const CLOSE_KEEP_MODEL_MODALS = [
  'multi-file-upload',
  'address-capture',
  'proof-of-identity-overlay',
  'right-to-rent',
  'additional-details',
  'currency-details',
  'subject-details',
  'terms-and-conditions',
] as const

export type ThemeOptionsDefault = {
  mainColor: string
  secondaryColor: string
  mainBtnColor: string
  secondaryBtnColor: string
  defaultAccent: string
  fontColor: string
  suggestedActionHighlight: string
  bubbleFromUserTextColor: string
  avatarSize: string
  suggestedActionBorderRadius: string
  suggestedActionFontWeight: string
  sendBoxBackground: string
  botAvatarImage: string
  userAvatarImage: string
  sendMessageColour: string
  agentBot: boolean
  primaryHeaderText?: string
  secondaryHeaderText?: string
  bubbleBackground?: string
  userBubbleBackground?: string
}

export type ThemeOptions = {
  default: ThemeOptionsDefault
  config?: Record<string, unknown>
}

export type BotViewState = {
  model: Record<string, unknown>
  showModal: boolean
  showOverlay: boolean
  hasOverlayToggle: boolean
  themeOptions: ThemeOptions
  sendBoxDisabled: boolean
}

export const initialThemeOptions: ThemeOptions = {
  default: {
    mainColor: '',
    secondaryColor: '',
    mainBtnColor: '',
    secondaryBtnColor: '',
    defaultAccent: '',
    fontColor: '',
    suggestedActionHighlight: '',
    bubbleFromUserTextColor: '',
    avatarSize: '',
    suggestedActionBorderRadius: '',
    suggestedActionFontWeight: '',
    sendBoxBackground: '',
    botAvatarImage: '',
    userAvatarImage: '',
    sendMessageColour: '',
    agentBot: false,
  },
}

export const initialBotViewState: BotViewState = {
  model: {},
  showModal: false,
  showOverlay: false,
  hasOverlayToggle: false,
  themeOptions: initialThemeOptions,
  sendBoxDisabled: false,
}

type ThemeColorPayload = {
  color: {
    primaryActive: string
    botAvatarImage: string
    secondaryActive: string
    mainBodyText: string
    primaryHeaderText: string
    secondaryHeaderText: string
    mainHighlight: string
    secondaryHighlight: string
    warningHighlight: string
  }
}

export const deriveThemeOptions = (
  current: ThemeOptions,
  payload: ThemeColorPayload,
): ThemeOptions => {
  const {
    primaryActive,
    botAvatarImage,
    secondaryActive,
    mainBodyText,
    primaryHeaderText,
    mainHighlight,
    secondaryHighlight,
  } = payload.color

  return {
    ...current,
    default: {
      ...current.default,
      mainColor: primaryActive,
      mainBtnColor: primaryActive,
      defaultAccent: mainHighlight,
      botAvatarImage,
      secondaryColor: secondaryActive,
      secondaryBtnColor: secondaryActive,
      fontColor: mainBodyText,
      primaryHeaderText,
      secondaryHeaderText: mainBodyText,
      bubbleFromUserTextColor: 'white',
      sendMessageColour: secondaryActive,
      bubbleBackground: mainHighlight,
      userBubbleBackground: secondaryHighlight,
    },
  }
}

export const mergeConfigIntoThemeOptions = (
  current: ThemeOptions,
  payload: Record<string, unknown>,
): ThemeOptions => {
  const config = { ...payload }
  delete config.themes
  return {
    ...current,
    config,
  }
}

const shouldClearModelOnClose = (model: Record<string, unknown>): boolean => {
  const modal = model.modal
  return (
    typeof modal !== 'string' ||
    !(CLOSE_KEEP_MODEL_MODALS as readonly string[]).includes(modal)
  )
}

const botViewSlice = createSlice({
  name: 'botView',
  initialState: initialBotViewState,
  reducers: {
    modelOpened(state, action: PayloadAction<Record<string, unknown>>) {
      state.model = { ...action.payload }
      state.showModal = true
      state.showOverlay = false
      state.sendBoxDisabled = true
    },
    overlayOpened(state, action: PayloadAction<Record<string, unknown>>) {
      state.showModal = false
      state.sendBoxDisabled = action.payload.modal === 'terms-and-conditions'
      state.model = { ...action.payload }
      const withToggle = Boolean(action.payload.withToggle)
      state.showOverlay = !withToggle
      state.hasOverlayToggle = withToggle
    },
    closed(state) {
      state.showModal = false
      state.showOverlay = false
      if (shouldClearModelOnClose(state.model)) {
        state.model = {}
        state.sendBoxDisabled = false
      } else if (state.model.modal !== 'terms-and-conditions') {
        state.sendBoxDisabled = false
      }
    },
    overlaySubmitted(state) {
      state.showModal = false
      state.showOverlay = false
      state.model = {}
      state.hasOverlayToggle = false
      state.sendBoxDisabled = false
    },
    resetForNewSession(state) {
      state.model = {}
      state.showModal = false
      state.showOverlay = false
      state.hasOverlayToggle = false
      state.sendBoxDisabled = false
    },
    overlayToggled(state) {
      state.showOverlay = !state.showOverlay
    },
    backToIncomeVerification(state) {
      state.hasOverlayToggle = false
      state.model = {}
      state.showModal = false
      state.showOverlay = false
      state.sendBoxDisabled = false
    },
    themeUpdated(state, action: PayloadAction<ThemeColorPayload>) {
      state.themeOptions = deriveThemeOptions(state.themeOptions, action.payload)
    },
    configUpdated(state, action: PayloadAction<Record<string, unknown>>) {
      state.themeOptions = mergeConfigIntoThemeOptions(
        state.themeOptions,
        action.payload,
      )
    },
    agentBotSet(state, action: PayloadAction<boolean>) {
      state.themeOptions = {
        ...state.themeOptions,
        default: {
          ...state.themeOptions.default,
          agentBot: action.payload,
        },
      }
    },
  },
})

export const {
  modelOpened,
  overlayOpened,
  closed,
  overlaySubmitted,
  resetForNewSession,
  overlayToggled,
  backToIncomeVerification,
  themeUpdated,
  configUpdated,
  agentBotSet,
} = botViewSlice.actions

export const selectBotView = (state: { botView: BotViewState }) => state.botView

export default botViewSlice.reducer
